import { Box, Grid, Typography, Button, Dialog, DialogTitle, DialogActions, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useLoading } from '@/hooks/useLoading';
import { LongLoading } from '@/components/Elements/Loading/LongLoading';
import { useEffect, useState } from 'react';
import awsconfig from '../../../amplifyconfiguration.json';
import { Amplify } from 'aws-amplify';
import { STATUS_COLOR } from '../constants/constants';
import ErrorDialog from '@/features/misc/ErrorDialog';
import { parseDate, parseQuestionnaireResponseStatus, parseTime } from '../func/parse';
import { useMedicalHealthInformationContext } from '../Context';
import { notMatchCustomErrorMessage } from '@/lib/sentryClient';
import * as Sentry from '@sentry/react';

Amplify.configure(awsconfig);

const MedicalHealthInformationDetail = () => {
  const [errorDialogOpen, setErrorDialogOpen] = useState<boolean>(false);
  const [errorDialogMessage, setErrorDialogMessage] = useState<string>('');
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [allGetButtonFlg, setAllGetButtonFlg] = useState<boolean>(false);
  const [allGetButtonDisplay, setAllGetButtonDisplay] = useState<boolean>(true);
  const { loadingEvent, Loading } = useLoading();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const {
    fetchMedicalHealthInformationDetail1,
    detailItemList,
    activeMedicalHealthInformation,
    activeQuestionnaireResponse,
    fhirIdForUnmask
  } = useMedicalHealthInformationContext();

  useEffect(() => {
    const fetchInformationList = () => {
      loadingEvent(async () => {
        try {
          await fetchMedicalHealthInformationDetail1(
            fhirIdForUnmask!,
            activeMedicalHealthInformation.facility_id.toString(),
            false // マスクした情報を取得
          );
        } catch (error) {
          if (error instanceof Error) {
            setErrorDialogMessage(error.message);
            setErrorDialogOpen(true);
          }
        }
      });
    };
    fetchInformationList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeQuestionnaireResponse.id]);

  const getMedicalHealthALLInfo = async () => {
    setIsLoading(true);
    try {
      await fetchMedicalHealthInformationDetail1(
        fhirIdForUnmask!,
        activeMedicalHealthInformation.facility_id.toString(),
        true // マスク解除した情報を取得
      );
      setAllGetButtonDisplay(false);
    } catch (error) {
      if (error instanceof Error) {
        if (notMatchCustomErrorMessage(error.name)) {
          Sentry.captureException(error);
        }
        setErrorDialogMessage(error.message);
        setErrorDialogOpen(true);
      }
    } finally {
      setIsLoading(false);
    }
    setOpenDialog(false);
    setAllGetButtonFlg(false);
  };

  const closeDialog = () => {
    setOpenDialog(false);
    setAllGetButtonFlg(false);
  };

  return (
    <Loading>
      {isLoading === false ? (
        <Box sx={{ marginTop: '12px' }}>
          <Grid container direction={'row'} spacing={2}>
            <Grid item xs={0} md={3} sm={2} />
            <Grid item xs={12} md={6} sm={8}>
              <Grid container rowSpacing={2} sx={{ textAlign: 'center', justifyContent: 'center' }}>
                <Grid item xs={11}>
                  <Box sx={{ padding: '8px', backgroundColor: 'white' }}>
                    <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'space-between' }}>
                      <Box sx={{ marginLeft: '10px', marginTop: '8px', fontWeight: 600, textAlign: 'left' }}>
                        <p style={{ fontSize: '10px' }}>
                          {`予約 : ${parseDate(activeMedicalHealthInformation.reserve_date)} ${parseTime(
                            activeMedicalHealthInformation.reserve_time
                          )}`}
                        </p>
                        <p style={{ fontSize: '14px' }}>{activeMedicalHealthInformation.facility_name}</p>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
                {detailItemList.length > 0 && (
                  <>
                    <Grid item xs={10} sx={{ textAlign: 'left' }}>
                      {detailItemList.map((item, index) => (
                        <Box sx={{ marginBottom: '15px' }} key={index}>
                          <Typography className="text-primary font-semibold text-lg" sx={{ textAlign: 'left' }}>
                            {item.category}
                          </Typography>
                          <Box sx={{ marginLeft: '15px', marginTop: '10px' }}>
                            {item.items.map((q, idx) => (
                              <Box sx={{ textAlign: 'left', marginBottom: '10px' }} key={idx}>
                                <Typography className="text-lg  font-medium text-black">{q.label}</Typography>
                                <Typography className="text-base text-[#424242]">
                                  {q.value}
                                  {q.unit && <span>{q.unit}</span>} {/* unitが存在する場合に表示 */}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      ))}
                    </Grid>
                    {/* 1件でもマスキングデータがあれば表示させる */}
                    {detailItemList.some((item) => item.is_masked) && allGetButtonDisplay && (
                      <Grid container rowSpacing={2} direction={'column'}>
                        <Grid item className="mx-4">
                          <Button
                            className={
                              allGetButtonFlg
                                ? 'text-black bg-gray-300 py-2 font-semibold w-full'
                                : 'text-white bg-primary py-2 font-semibold w-full'
                            }
                            onClick={() => {
                              setOpenDialog(true);
                              setAllGetButtonFlg(true);
                            }}
                            disabled={allGetButtonFlg}
                          >
                            全情報取得
                          </Button>
                        </Grid>
                        <Grid item>
                          <Typography className="text-sm" color="primary">
                            取得には2~3分ほどかかります
                          </Typography>
                        </Grid>
                      </Grid>
                    )}
                  </>
                )}
              </Grid>
            </Grid>
            <ErrorDialog
              open={errorDialogOpen}
              title={'医療健康情報詳細エラー'}
              buttonLabel="閉じる"
              buttonAction={() => {
                setErrorDialogOpen(false);
              }}
            >
              {errorDialogMessage}
            </ErrorDialog>
            <Dialog fullWidth open={openDialog} onClose={closeDialog}>
              <IconButton
                aria-label="close"
                onClick={closeDialog}
                sx={(theme) => ({
                  position: 'relative',
                  padding: 0,
                  marginLeft: 'auto',
                  paddingRight: '8px',
                  paddingTop: '8px',
                  color: theme.palette.grey[500]
                })}
              >
                <CloseIcon />
              </IconButton>
              <DialogTitle sx={{ fontSize: '16px', fontWeight: 'bold', padding: 0 }}>
                <Box display="flex" justifyContent="center">
                  本当に取得しますか？
                </Box>
              </DialogTitle>
              <DialogActions sx={{ justifyContent: 'center', padding: 2 }}>
                <Box display="flex" flexDirection="column" width="70%">
                  <Button className="bg-primary font-semibold" variant="contained" onClick={getMedicalHealthALLInfo}>
                    取得する
                  </Button>
                  <Button variant="outlined" color="error" sx={{ marginY: 2 }} onClick={closeDialog}>
                    キャンセル
                  </Button>
                </Box>
              </DialogActions>
            </Dialog>
          </Grid>
        </Box>
      ) : (
        <LongLoading></LongLoading>
      )}
    </Loading>
  );
};
export default MedicalHealthInformationDetail;
